import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import supabase from '../lib/supabase';
import SEO from '../components/SEO';

type Stage = 'verifying' | 'form' | 'success' | 'error';

const ResetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isItalian = i18n.language.startsWith('it');

  const [stage, setStage] = useState<Stage>('verifying');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [flowType, setFlowType] = useState<'recovery' | 'invite' | 'signup' | null>(null);

  useEffect(() => {
    const parseParams = () => {
      const rawHash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
      const hashParams = rawHash ? new URLSearchParams(rawHash) : null;
      const searchParams = location.search ? new URLSearchParams(location.search) : null;

      const params = hashParams && hashParams.has('type') ? hashParams : searchParams;
      const type = params?.get('type');
      const accessToken = params?.get('access_token');
      const refreshToken = params?.get('refresh_token');

      return { type, accessToken, refreshToken };
    };

    const { type, accessToken, refreshToken } = parseParams();

    if ((type === 'recovery' || type === 'invite') && accessToken && refreshToken) {
      setFlowType(type === 'invite' ? 'invite' : 'recovery');
      supabase.auth
        .setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error: sessionError }) => {
          if (sessionError) {
            setError(sessionError.message);
            setStage('error');
          } else {
            window.history.replaceState({}, '', '/reset-password');
            setStage('form');
          }
        });
    } else if (type === 'signup') {
      setFlowType('signup');
      setStage('success');
      setTimeout(() => navigate('/login'), 2000);
    } else {
      setError(
        isItalian
          ? 'Il link di reset non è valido o è scaduto. Richiedi un nuovo reset dalla pagina login.'
          : 'The reset link is invalid or expired. Request a new link from the login page.'
      );
      setStage('error');
    }
  }, [location, isItalian, navigate]);

  const labels = {
    title: isItalian ? 'Reimposta password' : 'Reset password',
    intro:
      stage === 'verifying'
        ? isItalian
          ? 'Verifica del link in corso…'
          : 'Verifying reset link…'
        : '',
    password: isItalian ? 'Nuova password' : 'New password',
    confirm: isItalian ? 'Conferma password' : 'Confirm password',
    submit: isItalian ? 'Aggiorna password' : 'Update password',
    success:
      flowType === 'signup'
        ? (isItalian
            ? 'Email confermata. Verrai reindirizzato alla pagina di login.'
            : 'Email confirmed. Redirecting you to the login page.')
        : (isItalian
            ? 'Password aggiornata con successo. Verrai reindirizzato alla pagina di login.'
            : 'Password updated successfully. Redirecting you to the login page.'),
    requirements: isItalian
      ? 'La password deve contenere almeno 8 caratteri.'
      : 'Password must be at least 8 characters long.',
    mismatch: isItalian ? 'Le password non coincidono.' : 'Passwords do not match.',
    genericError: isItalian
      ? 'Qualcosa è andato storto. Riprova o richiedi un nuovo link.'
      : 'Something went wrong. Try again or request a new link.',
    backToLogin: isItalian ? 'Torna al login' : 'Back to login',
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(labels.requirements);
      return;
    }

    if (password !== confirmPassword) {
      setError(labels.mismatch);
      return;
    }

    setStage('verifying');
    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(updateError.message ?? labels.genericError);
      setStage('form');
      return;
    }

    setFlowType(flowType || 'recovery');
    setStage('success');
    setTimeout(() => navigate('/login'), 2500);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-800 dark:bg-gray-900 text-gray-900 dark:text-gray-100 dark:text-gray-100 flex items-center justify-center px-4 py-12">
      <SEO
        title={labels.title}
        description={isItalian ? 'Imposta una nuova password per il tuo account.' : 'Choose a new password for your account.'}
        canonical="https://victorlazari.com/reset-password"
      />
      <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 shadow-xl">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4 leading-tight">{labels.title}</h1>

        {stage === 'verifying' && (
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{labels.intro}</p>
        )}

        {stage === 'form' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1 font-medium" htmlFor="password">
                {labels.password}
              </label>
              <input
                id="password"
                type="password"
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                minLength={8}
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1 font-medium" htmlFor="confirmPassword">
                {labels.confirm}
              </label>
              <input
                id="confirmPassword"
                type="password"
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                minLength={8}
                required
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{labels.requirements}</p>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button
              type="submit"
              className="w-full rounded-lg bg-cyan-500 py-2 text-sm font-semibold text-white transition-colors hover:bg-cyan-600 shadow-md"
            >
              {labels.submit}
            </button>
          </form>
        )}

        {stage === 'success' && (
          <div className="space-y-3">
            <p className="text-sm text-emerald-600 leading-relaxed">{labels.success}</p>
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-semibold text-cyan-600 hover:text-cyan-700"
            >
              {labels.backToLogin}
            </button>
          </div>
        )}

        {stage === 'error' && (
          <div className="space-y-3">
            {error && <p className="text-sm text-red-600 leading-relaxed">{error}</p>}
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-semibold text-cyan-600 hover:text-cyan-700"
            >
              {labels.backToLogin}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;



